import React from "react";
import { Copy, Terminal, ShieldAlert } from "lucide-react";
import { cn } from "../../lib/utils";
import { MapEditorPaneHeader, MapEditorSummaryList } from "./internal/MapEditorKitParts";
import type { MapDiagnosticsPaneProps } from "../../contracts/map-workspace";

export function MapDiagnosticsPane({
  generatedJson,
  summaryItems = [],
  onCopyJson,
  className,
}: MapDiagnosticsPaneProps) {
  return (
    <div className={cn("flex flex-col w-full h-full bg-background overflow-hidden", className)}>
      <div className="p-4 h-full flex flex-col">
        <MapEditorPaneHeader
          title="System Diagnostics"
          description="Inspect the generated runtime payload and the current editor state."
          icon={<Terminal className="h-4 w-4" />}
          actions={
            onCopyJson ? (
              <button
                onClick={onCopyJson}
                className="flex items-center gap-1.5 rounded-control border border-border bg-muted px-2 py-1 text-[10px] font-bold transition-all hover:bg-muted/80"
                title="Copy configuration JSON"
              >
                <Copy className="h-3 w-3" />
                Copy JSON
              </button>
            ) : undefined
          }
          className="mb-4"
        />

        {summaryItems.length > 0 && (
          <MapEditorSummaryList
            className="mb-6"
            items={summaryItems.map(item => ({
              key: item.id,
              label: item.label,
              value: item.value,
            }))}
          />
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-muted/15">
          <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2">
            <span className="text-[10px] font-mono uppercase tracking-tighter text-muted-foreground/60">
              Runtime Config (read-only)
            </span>
            <div className="flex gap-1.5">
              <div className="h-2 w-2 rounded-full bg-destructive/20" />
              <div className="h-2 w-2 rounded-full bg-primary/20" />
              <div className="h-2 w-2 rounded-full bg-foreground/20" />
            </div>
          </div>
          
          <div className="flex-1 overflow-auto p-4 scrollbar-thin scrollbar-thumb-muted">
            <pre className="text-[11px] leading-relaxed text-foreground selection:bg-primary/15">
              <code>{generatedJson}</code>
            </pre>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 p-2 rounded-sm bg-amber-500/5 border border-amber-500/20">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-[9px] text-amber-700/80 leading-snug italic font-medium">
            This data reflects the current client-side state of the map workspace and may diverge from the source of truth until saved.
          </p>
        </div>
      </div>
    </div>
  );
}
